/** Create or edit one schedule. Full screen on phones (a keyboard form), a
 * right-hand panel on desktop. `schedule === null` is create mode. The
 * pristine snapshot is state, not a ref: the dirty check is a memo over it. */
import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '../../../shared/ui/button';
import { Label } from '../../../shared/ui/label';
import { Textarea } from '../../../shared/ui/textarea';
import { Sheet, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle } from '../../../shared/ui/sheet';
import { DeliverableSelect, isDeliverableRefusal, noteDeliverableRefusal, useDeliverableOffered } from './deliverable';
import { TimeSelects } from './TimeSelects';
import { cn } from '../../../shared/ui/utils';
import { botTaskSchedulesService, type BotSchedule, type ScheduleDelivery, type ScheduleFrequency } from '../../../services/BotTaskSchedulesService';
import {
  FREQUENCY_OPTIONS, PROMPT_MAX, SELECT_CLASS, WEEKDAY_LABELS, WEEKDAY_NAMES, draftPatch, draftProblem, draftToPayload, emptyDraft,
  sameScheduleDraft, toScheduleDraft, toggleWeekday, type ScheduleDraft,
} from './scheduleForm';


interface BotScheduleFormProps {
  open: boolean;
  /** Fixed when the form is opened from one bot. Omit it to pick from `bots`. */
  botId?: string;
  botName?: string;
  bots?: { id: string; name: string }[];
  workspaceId: string;
  schedule: BotSchedule | null;
  onClose: () => void;
  onSaved: (schedule: BotSchedule) => void;
}

export function BotScheduleForm(props: BotScheduleFormProps) {
  const { open, onClose } = props;
  return (
    <Sheet open={open} onOpenChange={(next) => { if (!next) onClose(); }}>
      <SheetContent className="h-[100dvh] w-full gap-0 sm:max-w-none lg:w-[480px] lg:max-w-[480px]">
        {/* Mounted only while open, so each opening starts from a fresh baseline. */}
        {open && <FormBody {...props} />}
      </SheetContent>
    </Sheet>
  );
}

function FormBody({ botId: fixedBotId, botName: fixedBotName, bots = [], workspaceId, schedule, onClose, onSaved }: BotScheduleFormProps) {
  const picking = !fixedBotId && !schedule;
  const [pickedBotId, setPickedBotId] = useState(bots.length === 1 ? bots[0].id : '');
  const botId = fixedBotId ?? schedule?.botId ?? pickedBotId;
  const botName = fixedBotName ?? schedule?.botName ?? bots.find((bot) => bot.id === botId)?.name ?? 'The bot';
  const [baseline] = useState<ScheduleDraft>(() => (schedule ? toScheduleDraft(schedule) : emptyDraft()));
  const [draft, setDraft] = useState<ScheduleDraft>(baseline);
  const [saving, setSaving] = useState(false);
  // An existing schedule that already has a file keeps its control even after a refusal.
  const offerDeliverable = useDeliverableOffered(workspaceId) || Boolean(baseline.deliverable);
  // An edit keeps the zone the schedule was made in; a new one uses this browser's.
  const zone = schedule?.timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;
  const hasChanges = useMemo(() => !sameScheduleDraft(draft, baseline), [draft, baseline]);
  const problem = (picking && !botId ? 'Pick a bot.' : null) ?? draftProblem(draft);
  const patch = <K extends keyof ScheduleDraft>(key: K, value: ScheduleDraft[K]) => setDraft((d) => ({ ...d, [key]: value }));

  const save = async () => {
    if (problem || saving || (schedule && !hasChanges)) return;
    setSaving(true);
    try {
      const saved = schedule
        ? await botTaskSchedulesService.update(schedule.id, draftPatch(draft, baseline))
        : await botTaskSchedulesService.create({ ...draftToPayload(draft), botId, workspaceId });
      toast.success(schedule ? 'Schedule saved' : 'Schedule created');
      onSaved(saved);
      onClose();
    } catch (err) {
      if (draft.deliverable) {
        noteDeliverableRefusal(workspaceId, err);
        // The control may now be hidden: do not resend what was refused.
        if (isDeliverableRefusal(err)) patch('deliverable', baseline.deliverable);
      }
      toast.error(err instanceof Error ? err.message : 'The schedule could not be saved.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form className="flex min-h-0 flex-1 flex-col font-apple" onSubmit={(event) => { event.preventDefault(); void save(); }}>
      <SheetHeader className="pr-12">
        <SheetTitle>{schedule ? 'Edit schedule' : 'Run on a schedule'}</SheetTitle>
        <SheetDescription>
          {botName} does this as you and never changes anything in Quantis without your approval. Changes wait for you and lapse after 7 days.
        </SheetDescription>
      </SheetHeader>

      <div className="min-h-0 flex-1 space-y-5 overflow-y-auto px-4 pb-4">
        {picking && (
          <div className="space-y-2">
            <Label htmlFor="schedule-bot">Bot</Label>
            <select id="schedule-bot" className={SELECT_CLASS} value={pickedBotId} onChange={(e) => setPickedBotId(e.target.value)}>
              {bots.length !== 1 && <option value="">Choose a bot</option>}
              {bots.map((bot) => <option key={bot.id} value={bot.id}>{bot.name}</option>)}
            </select>
          </div>
        )}

        <div className="space-y-2">
          <Label htmlFor="schedule-prompt">What should it do</Label>
          <Textarea id="schedule-prompt" rows={5} value={draft.prompt} maxLength={PROMPT_MAX} onChange={(e) => patch('prompt', e.target.value)}
            placeholder="Summarise yesterday's site diaries and list anything overdue" className="text-base lg:text-sm" />
        </div>

        {offerDeliverable && <DeliverableSelect id="schedule-file" value={draft.deliverable} onChange={(value) => patch('deliverable', value)} />}

        <div className="space-y-2">
          <Label htmlFor="schedule-frequency">Repeat</Label>
          <select id="schedule-frequency" className={SELECT_CLASS} value={draft.frequency} onChange={(e) => patch('frequency', e.target.value as ScheduleFrequency)}>
            {FREQUENCY_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
          </select>
        </div>

        {draft.frequency === 'weekly' && (
          <fieldset className="space-y-2">
            <legend className="text-sm font-medium">On</legend>
            <div className="flex flex-wrap gap-1.5">
              {WEEKDAY_LABELS.map((label, index) => {
                const day = index + 1;
                const on = draft.weekdays.includes(day);
                return (
                  <button key={label} type="button" aria-pressed={on} aria-label={WEEKDAY_NAMES[index]} onClick={() => patch('weekdays', toggleWeekday(draft.weekdays, day))}
                    className={cn('h-9 min-w-11 rounded-md border px-2 text-sm', on ? 'border-transparent bg-primary text-primary-foreground' : 'border-separator bg-background')}>
                    {label}
                  </button>
                );
              })}
            </div>
          </fieldset>
        )}

        {draft.frequency === 'monthly' && (
          <div className="space-y-2">
            <Label htmlFor="schedule-month-day">Day of the month</Label>
            <select id="schedule-month-day" className={SELECT_CLASS} value={draft.monthDay} onChange={(e) => patch('monthDay', Number(e.target.value))}>
              {Array.from({ length: 28 }, (_, i) => i + 1).map((day) => <option key={day} value={day}>{day}</option>)}
            </select>
          </div>
        )}

        <fieldset className="space-y-2">
          <legend className="text-sm font-medium">At</legend>
          <TimeSelects value={draft.time} onChange={(time) => patch('time', time)} />
          <p className="text-xs text-muted-foreground">24-hour, {zone}</p>
        </fieldset>

        <div className="space-y-2">
          <Label htmlFor="schedule-deliver">Send the result as</Label>
          <select id="schedule-deliver" className={SELECT_CLASS} value={draft.deliverTo} onChange={(e) => patch('deliverTo', e.target.value as ScheduleDelivery)}>
            <option value="chat">A direct message from {botName}</option>
            <option value="notification">A notification</option>
          </select>
          <p className="text-xs text-muted-foreground">Daily is the most often a schedule can run.</p>
        </div>

        {hasChanges && problem && problem !== 'Pick a bot.' && <p role="alert" className="text-sm text-destructive">{problem}</p>}
      </div>

      {(hasChanges || saving) && <SheetFooter className="flex-row justify-end gap-2 border-t border-separator pb-[max(1rem,env(safe-area-inset-bottom))]">
        <Button type="button" variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
        <Button type="submit" disabled={saving || Boolean(problem) || (schedule !== null && !hasChanges)}>{saving ? 'Saving' : 'Save'}</Button>
      </SheetFooter>}
    </form>
  );
}
